import { Box, ScrollArea, SemiCircleProgress, Text } from '@mantine/core'
import { useNavigate } from 'react-router-dom'
import { useUserNextLevel } from '../../api/hooks/User/useUserNextLevel'
import { useUserStep } from '../../api/hooks/User/useUserStep'
import { useAuthStore } from '../../store/useAuthStore'
import { useLearningStore } from '../../store/useLearningStore'
import styles from '../../styles/user/ActivitySummaryBoxMobile.module.scss'
import Avatar from '../common/Avatar'
import BaseButton from '../common/BaseButton'
import { resolveAvatarSrc } from '../../utils/avatar'

const ActivitySummaryBoxMobile = () => {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const updateUserPartial = useAuthStore((state) => state.updateUserPartial)
  const nextLevelMutation = useUserNextLevel()
  const userStepMutation = useUserStep()

  const { questions, userAnswers, getAnswerSummary } = useLearningStore()

  const questionData = questions.map((question) => {
    const userAnswer = userAnswers.find(
      (answer) => answer.questionId === question.QUESTION_ID
    )
    return {
      id: question.QUESTION_ID,
      text: `Question ${question.NO}`,
      title: question.TITLE,
      isCorrect: userAnswer?.isCorrect || false,
    }
  })

  const summary = getAnswerSummary()
  const percentage =
    questions.length > 0
      ? Math.round((summary.correct / summary.total) * 100)
      : 0

  const handleStartNext = async () => {
    if (!user) return

    const nextStepData = {
      LEVEL_ID: user.LEVEL_ID,
      ID: user.ID,
      STEP: user.STEP,
    }

    try {
      const nextLevelResponse = await nextLevelMutation.mutateAsync(
        nextStepData
      )

      // 업데이트된 STEP 조회
      const stepResponse = await userStepMutation.mutateAsync({ ID: user.ID })

      // STEP 정보 추출
      let serverStep = null
      if (stepResponse.data && Array.isArray(stepResponse.data)) {
        if (stepResponse.data[0] && stepResponse.data[0].STEP) {
          serverStep = stepResponse.data[0].STEP
        }
      } else if (stepResponse.data && stepResponse.data.STEP) {
        serverStep = stepResponse.data.STEP
      }

      if (serverStep !== null && typeof serverStep === 'number') {
        updateUserPartial({ STEP: serverStep })

        if (serverStep % 5 === 0) {
          navigate('/learningQuizOnly')
        } else {
          navigate('/solver')
        }
      } else {
        navigate('/solver')
      }
    } catch (error) {
      navigate('/solver')
    }
  }

  return (
    <div className={styles.summaryBoxContainer}>
      <p className={styles.title}>Activity summary</p>

      <div className={styles.topSection}>
        <div className={styles.avatarSection}>
          <img
            src={resolveAvatarSrc(user?.AVATAR?.trim() || '')}
            alt={user?.AVATAR?.trim() ? '유저 아바타' : '기본 아바타'}
            className={styles.avatar}
            onError={(e) => {
              e.currentTarget.src = '/assets/avatar/characters/Buddy.png'
            }}
          />
          <div className={styles.userName}>
            {user?.NICKNAME || user?.NAME || 'name'}
          </div>
        </div>

        <div className={styles.questionSection}>
          <p className={styles.lessonTitle} style={{ marginTop: '5px' }}>
            Lesson topic
          </p>

          <div className={styles.questionListContainer}>
            <ScrollArea
              w={150}
              h={120}
              type="always"
              scrollHideDelay={2000}
              scrollbarSize={2}
              styles={{
                thumb: {
                  backgroundColor: '#c1c2c5',
                  borderRadius: '3px',
                },
              }}
            >
              <div className={styles.tableContainer}>
                <table className={styles.questionTable}>
                  <tbody>
                    {questionData.map((question) => (
                      <tr key={question.id} className={styles.questionRow}>
                        <td className={styles.questionCell}>
                          <div className={styles.questionText}>
                            {question.text}
                          </div>
                        </td>
                        <td className={styles.resultCell}>
                          <div className={styles.resultIndicator}>
                            {question.isCorrect ? (
                              <div className={styles.correctIndicator}>O</div>
                            ) : (
                              <div className={styles.incorrectIndicator}>X</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>

      <div className={styles.bottomSection}>
        <div className={styles.chartSection}>
          <div className={styles.chartContainer}>
            <Box className={styles.chartBox}>
              <div className={styles.counts}>
                {summary.correct}/{summary.total}
              </div>
              <div className={styles.semiCircleProgressWrapper}>
                <SemiCircleProgress
                  size={220}
                  thickness={35}
                  value={percentage}
                  color="#1ED760"
                  label={null}
                />
              </div>
              <Box className={styles.labelBox}>
                <Text className={styles.percentageLabel}>{percentage}%</Text>
              </Box>
            </Box>
          </div>
        </div>

        <div className={styles.buttonWrapper}>
          <BaseButton className={styles.baseButton} onClick={handleStartNext}>
            Start
          </BaseButton>
        </div>
      </div>
    </div>
  )
}

export default ActivitySummaryBoxMobile
